import {createContext, PropsWithChildren, useMemo, useState, useEffect, useRef, useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {authStatus} from '@/services/redux/selectors/authSelectors';
import {SOCKET, SUCCESS_KEY, toastDuration} from '@/constants';
import {getWsToken} from '@/services/api';
import {GetWsTokenSuccessResponse, SocketEvent} from '@/interface';
import {SocketManager} from '@/services/socket/SocketManager';
import {setEnsureWebSocketConnectionHandler} from './webSocketBridge';

import ToastContext, {ToastContextType} from './ToastContext';
import {ToastType} from '@/ui-kits';

export interface WebSocketContextInterface {
  isConnectionOpen: boolean;
  sendMessage: (message: SocketEvent) => void;
}

const WebSocketContext = createContext<WebSocketContextInterface>({
  isConnectionOpen: false,
  sendMessage: () => {},
});

export const WebSocketProvider: React.FC<PropsWithChildren> = props => {
  const {children} = props;

  // ==============
  // hooks
  // ==============

  const {addToast} = useContext<ToastContextType>(ToastContext);
  const dispatch = useDispatch();

  // ==============
  // selectors
  // ==============
  const isAuthenticated = useSelector(authStatus);

  // ==============
  // state
  // ==============
  const [isConnectionOpen, setIsConnectionOpen] = useState<boolean>(false);
  const socketManagerRef = useRef<SocketManager | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const isConnectionOpenRef = useRef<boolean>(false);
  isConnectionOpenRef.current = isConnectionOpen;

  // ==============
  // functions
  // ==============
  const getWsUrl = useCallback(async (): Promise<string | null> => {
    // get ephemeral ws token
    let token = '';
    const response = await getWsToken();
    const data = response.data as GetWsTokenSuccessResponse;
    if (data.status === SUCCESS_KEY) {
      token = data.data.ws_token;
    }

    // if token is not found, return null
    if (!token) {
      return null;
    }

    // establish websocket connection with the server using ephemeral ws token
    const socketBaseUrl = SOCKET.currentEnv ? SOCKET.baseUrls[SOCKET.currentEnv as keyof typeof SOCKET.baseUrls] : SOCKET.baseUrls.dev;

    const params = new URLSearchParams({token});

    const socketUrl = `${socketBaseUrl}${SOCKET.socketUrl.ws}?${params.toString()}`;
    return socketUrl;
  }, []);

  const sendMessage = (msg: SocketEvent) => {
    socketManagerRef.current?.send(msg);
  };

  const showGlobalToast = (message: string, type: ToastType) => {
    addToast(`global-${Date.now()}`, '', message, type, toastDuration);
  };

  // ==============
  // side effects
  // ==============
  useEffect(() => {
    // if not authenticated, close the connection and unregister bridge
    if (!isAuthenticated) {
      setEnsureWebSocketConnectionHandler(null);
      socketManagerRef.current?.close();
      socketManagerRef.current = null;
      isConnectingRef.current = false;
      return;
    }

    // establish the connection
    socketManagerRef.current ??= new SocketManager(dispatch, showGlobalToast, setIsConnectionOpen);

    const connect = async () => {
      if (isConnectingRef.current) return;
      if (socketManagerRef.current?.isConnecting()) return; // ← ADD THIS
      if (socketManagerRef.current?.isOpen()) return; // ← ADD THIS
      isConnectingRef.current = true;
      try {
        const url = await getWsUrl();
        if (url) {
          socketManagerRef.current?.connect(url);
        }
      } finally {
        isConnectingRef.current = false;
      }
    };

    connect();
    connect();

    // Register handler so API requests can trigger reconnect when disconnected
    setEnsureWebSocketConnectionHandler(() => {
      if (!isConnectionOpenRef.current && socketManagerRef.current && !isConnectingRef.current) {
        connect();
      }
    });

    return () => {
      setEnsureWebSocketConnectionHandler(null);
    };
  }, [isAuthenticated]);
  const contextValues = useMemo<WebSocketContextInterface>(() => {
    return {
      isConnectionOpen,
      sendMessage,
    };
  }, [isConnectionOpen]);
  return <WebSocketContext.Provider value={contextValues}>{children}</WebSocketContext.Provider>;
};

export default WebSocketContext;
